import { useEffect } from "react";
import { useConfigContext } from "../../../ConfigProvider.tsx";
import { useAutocomplete } from "../../context/GlobalContext";

function startAnimatedPlaceholderRotation(
    inputEl: HTMLInputElement | null,
    texts: string[],
    intervalMs = 4000,
    animDurationMs = 750
): ReturnType<typeof setInterval> | undefined {
    if (!inputEl || !texts.length) return;
    const wrapper = inputEl.closest('.searchBar');
    if (!wrapper) return;
    const container = wrapper.querySelector<HTMLDivElement>('.placeholder-container')!;
    inputEl.placeholder = '';
    let idx = 0;

    let current = document.createElement('span');
    current.className = 'placeholder-text current';
    current.textContent = texts[idx];
    container.appendChild(current);

    function rotate() {
        container.querySelectorAll<HTMLSpanElement>('.placeholder-text:not(.current)')
            .forEach((s) => container.removeChild(s));

        const nextIdx = (idx + 1) % texts.length;
        const next = document.createElement('span');
        next.className = 'placeholder-text';
        next.textContent = texts[nextIdx];
        container.appendChild(next);

        void next.getBoundingClientRect();
        current.classList.add('slide-out');
        next.classList.add('slide-in');

        setTimeout(() => {
            if (current.parentNode) container.removeChild(current);
            next.classList.remove('slide-in');
            next.classList.add('current');
            current = next;
            idx = nextIdx;
        }, animDurationMs);
    }

    rotate();
    return setInterval(rotate, intervalMs);
}

export function Form() {
    const { inputRef, formRef, autocomplete, isModalOpen, autocompleteState: { query } } = useAutocomplete();
    const { tc } = useConfigContext();

    const { onSubmit: algoliaOnSubmit, ...restFormProps } = autocomplete.getFormProps({ inputElement: inputRef.current });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        const value = inputRef.current?.value.trim() ?? "";
        if (!value) {
            event.preventDefault();
            return;
        }
        algoliaOnSubmit(event);
    }

    useEffect(() => {
        if (isModalOpen && inputRef?.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
        }
    }, [isModalOpen, inputRef]);

    useEffect(() => {
        const siteCode = document.body.getAttribute('data-site_code');
        const menuSelector = siteCode === 'portal'
            ? '.headerMainNavigation > ul.nav--shop > li.level_1 > a'
            : '.menu-products .megaMenu__navigation ul.level_2 > li.level_2 > a';

        const links = document.querySelectorAll<HTMLAnchorElement>(menuSelector);
        const lang = document.body.getAttribute('data-lang_code');
        const placeholders = Array.from(links, (a) => {
            const txt = a.textContent?.trim() ?? '';
            return lang !== 'de'
                ? `${tc.search} ${txt}...`
                : `${txt} suchen...`;
        });

        const input = inputRef.current;
        const wrapper = input?.closest('.searchBar');

        const handleFocus = () => wrapper?.classList.add('is-focus');
        const handleBlur = () => {
            wrapper?.classList.remove('is-focus');
            if (!input?.value) wrapper?.classList.remove('is-filled');
        };
        const handleInput = () => {
            if (input?.value) {
                wrapper?.classList.add('is-filled');
            } else {
                wrapper?.classList.remove('is-filled');
            }
        };

        input?.addEventListener('focus', handleFocus);
        input?.addEventListener('blur', handleBlur);
        input?.addEventListener('input', handleInput);

        let rotator: ReturnType<typeof setInterval> | undefined;
        if (input) {
            rotator = startAnimatedPlaceholderRotation(
                input,
                placeholders,
                4000,
                750
            );
        }

        return () => {
            if (rotator) clearInterval(rotator as unknown as number);
            input?.removeEventListener('focus', handleFocus);
            input?.removeEventListener('blur', handleBlur);
            input?.removeEventListener('input', handleInput);
        };
    }, [inputRef, tc.search]);

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="algoliaAutocompleteForm"
            {...restFormProps}
        >
            <div className="algoliaAutocompleteForm__searchBar inputGroup searchBar">
                <label
                    className="algoliaAutocompleteForm__label"
                    {...autocomplete.getLabelProps({})}
                >
                    <button
                        className="algoliaAutocompleteForm__submit inputGroup__addon"
                        type="submit"
                    >
                        <span className="sr-only">{tc.search}</span>
                        <i className="icon icon-search" aria-hidden="true"></i>
                    </button>
                </label>
                <input
                    ref={inputRef}
                    className="algoliaAutocompleteForm__input"
                    {...autocomplete.getInputProps({ inputElement: inputRef.current })}
                />
                {query && (
                    <button
                        className="algoliaAutocompleteForm__clear"
                        title="Clear"
                        type="reset"
                    >
                        <i className="icon icon-close" aria-hidden={true}></i>
                    </button>
                )}
                <div className="placeholder-container"></div>
            </div>
        </form>
    );
}
